/**
 * LINE NOTIFICATIONS, LAYER 0 (2026-09-14; docs/specs/2026-09-13-the-life-assistant-features.md
 * §6, the proactive channel). The browser's Notification API and nothing else: when a line of the
 * far end's arrives on the live feed and this tab is not the one being looked at, the line is shown
 * as a system notification, titled with the person's name and bodied with the line itself. Nothing
 * else ever notifies, and the text is never "you have a new message": a notification IS his line.
 *
 * Only live lines reach here (the session hook calls `notifyLine` from the feed handler, never
 * from the history hydrate), so a backlog on load cannot fire a stack of toasts. Tab open and
 * focused: nothing, the thread is already in front of the reader. Permission is asked only from a
 * button press, because browsers refuse it otherwise and because asking unprompted is the summons
 * this product refuses. The choice is per browser, in localStorage, like the locale.
 *
 * What this layer cannot do: reach a closed tab. That is Web Push (a service worker, VAPID keys, a
 * subscription store, a sender in Jobs) — layer 2, after the arrangement that fires (layer 1, the
 * engine) exists to give it something to send.
 */

const STORAGE_KEY = 'heimar.lineNotifications'

export function lineNotificationsSupported(): boolean {
  return typeof window !== 'undefined' && 'Notification' in window
}

/** On only when the browser has granted permission AND this reader switched it on here. */
export function lineNotificationsEnabled(): boolean {
  if (!lineNotificationsSupported() || Notification.permission !== 'granted') return false
  try {
    return localStorage.getItem(STORAGE_KEY) === 'on'
  } catch {
    return false
  }
}

/**
 * Ask (from a user gesture) and, if granted, switch on for this browser. Resolves to the state.
 * `confirm`, when given, is shown at once as a notification in the surface's own voice, so the
 * reader learns on the spot whether their system shows them at all — the one case where a
 * notification is not his line, because the reader just asked for it.
 */
export async function enableLineNotifications(confirm?: { title: string; body: string }): Promise<boolean> {
  if (!lineNotificationsSupported()) {
    console.warn('[heimar] line notifications: this browser has no Notification API')
    return false
  }
  const permission =
    Notification.permission === 'granted' ? 'granted' : await Notification.requestPermission()
  if (permission !== 'granted') {
    console.warn(`[heimar] line notifications: permission ${permission} (a past "block" for this site must be lifted in the browser's site settings)`)
    return false
  }
  try {
    localStorage.setItem(STORAGE_KEY, 'on')
  } catch {
    /* a browser that refuses storage still gets this session's notifications */
  }
  console.info('[heimar] line notifications: on')
  if (confirm) {
    try {
      new Notification(confirm.title, { body: confirm.body, tag: 'heimar-notify-on' })
    } catch (e) {
      console.warn('[heimar] line notifications: the confirmation could not be shown', e)
    }
  }
  return true
}

export function disableLineNotifications(): void {
  try {
    localStorage.setItem(STORAGE_KEY, 'off')
  } catch {
    /* nothing to undo */
  }
}

/**
 * Show one line as a notification, if this reader asked for them and is not looking at the tab.
 * `tag` is the message id, so a line the feed repeats is shown once.
 */
export function notifyLine(title: string, body: string, tag: string): void {
  if (!lineNotificationsEnabled()) {
    console.info('[heimar] line notifications: off, line not shown')
    return
  }
  if (document.visibilityState === 'visible' && document.hasFocus()) {
    console.info('[heimar] line notifications: tab in front, line not shown')
    return
  }
  try {
    const n = new Notification(title, { body, tag })
    n.onclick = () => {
      window.focus()
      n.close()
    }
    console.info('[heimar] line notifications: shown')
  } catch (e) {
    /* a platform that constructs notifications only from a service worker (older Android
       Chrome) throws here; layer 2 is its answer, and the thread still shows the line */
    console.warn('[heimar] line notifications: could not be shown', e)
  }
}
